import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Phone, TrendingUp, Clock3, BadgeCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useWizard } from "../components/WizardProvider";
import { MobileCtaBar } from "../components/MobileCtaBar";

const SERVICES = {
  "childcare-centres": {
    name: "Childcare Centres",
    category: "Childcare Centres",
    accent: "#FF1744",
    eyebrow: "TURNKEY CHILDCARE ASSETS",
    title: "Childcare Centres — Build & Lease",
    tagline: "Recession-resilient assets for NSW's fastest-growing family corridors.",
    desc: "From DA approvals to play-space engineering, we deliver turnkey, licence-ready early-learning facilities anchored by long-term commercial leases — the strongest yield story in NSW property.",
    hero: "https://images.pexels.com/photos/1115804/pexels-photo-1115804.jpeg?auto=compress&cs=tinysrgb&w=1920",
    stats: [
      { icon: TrendingUp, value: "~6.0%", label: "Indicative gross yield" },
      { icon: Clock3, value: "~14 mo", label: "Typical build timeline" },
      { icon: BadgeCheck, value: "15 yr", label: "Typical lease structure" },
    ],
    specs: [
      ["Site Requirement", "1,000 – 2,000 m² corner or high-exposure lots"],
      ["Licensed Places", "60 – 90 place centres"],
      ["Parking", "25+ compliant car spaces with safe drop-off"],
      ["Play Space", "7 m²+ unencumbered outdoor play per child"],
      ["Services", "Commercial kitchen, laundry & staff amenities"],
      ["Compliance", "NCC Class 9b, AS 1428 access, DA managed end-to-end"],
    ],
  },
  "residential-homes": {
    name: "Residential Homes",
    category: "Residential Homes",
    accent: "#D500F9",
    eyebrow: "CUSTOM HOMES · VILLAS · KNOCK-DOWN REBUILD",
    title: "Bespoke Residential Homes",
    tagline: "Your block, your light, your life — designed around the way you live.",
    desc: "Custom architectural homes, luxury villas and full knock-down rebuild solutions. We keep the address you love and replace the house with one engineered for the next fifty years of Aussie living.",
    hero: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=1920",
    stats: [
      { icon: TrendingUp, value: "~5.5%", label: "Indicative capital growth p.a." },
      { icon: Clock3, value: "~11 mo", label: "Typical build timeline" },
      { icon: BadgeCheck, value: "7-star", label: "Energy rating standard" },
    ],
    specs: [
      ["Configurations", "3 – 5+ bed, 2.5 – 3.5 bath layouts"],
      ["Garaging", "Double garage with internal access"],
      ["Outdoor Living", "Alfresco entertaining + outdoor kitchen ready"],
      ["Energy", "7-star NatHERS, solar & battery ready"],
      ["Interiors", "Stone benchtops, butler's pantry options"],
      ["KDR", "Full knock-down rebuild feasibility on your block"],
    ],
  },
  "multi-unit-developments": {
    name: "Multi-Unit Developments",
    category: "Multi-Unit Developments",
    accent: "#FFC107",
    eyebrow: "GATED ESTATES · DUPLEXES · TOWNHOUSES",
    title: "Multi-Unit Developments",
    tagline: "Medium-density communities where neighbours become mates.",
    desc: "Duplexes, townhouse clusters and gated masterplanned estates — secure, community-driven developments with parks, shared green space and modern amenity, staged to maximise return on every lot.",
    hero: "https://images.pexels.com/photos/1396132/pexels-photo-1396132.jpeg?auto=compress&cs=tinysrgb&w=1920",
    stats: [
      { icon: TrendingUp, value: "2 – 40", label: "Dwellings per project" },
      { icon: Clock3, value: "~5.8%", label: "Indicative duplex yield" },
      { icon: BadgeCheck, value: "Staged", label: "Settlement & release" },
    ],
    specs: [
      ["Product Mix", "Duplex, townhouse & villa combinations"],
      ["Title", "Torrens or strata subdivision managed"],
      ["Amenity", "Communal green space, BBQ & play pockets"],
      ["Parking", "Double garaging + visitor allocation"],
      ["Connected", "NBN, EV-charging & smart-meter ready"],
      ["Approvals", "CDC / DA pathway management included"],
    ],
  },
  "commercial-shop-fitouts": {
    name: "Commercial & Shop Fit-Outs",
    category: "Shop Fit-Outs",
    accent: "#00B0FF",
    eyebrow: "RETAIL · OFFICE · HOSPITALITY",
    title: "Commercial Complexes & Fit-Outs",
    tagline: "Spaces engineered for footfall, function and long-term tenancy.",
    desc: "From bare shells to premium trading environments — retail shopfronts, office suites and mixed-use commercial developments delivered fast, compliant and tenant-ready.",
    hero: "https://images.pexels.com/photos/323705/pexels-photo-323705.jpeg?auto=compress&cs=tinysrgb&w=1920",
    stats: [
      { icon: TrendingUp, value: "~6.8%", label: "Indicative gross yield" },
      { icon: Clock3, value: "6–12 wk", label: "Typical fit-out window" },
      { icon: BadgeCheck, value: "DDA", label: "Compliant access throughout" },
    ],
    specs: [
      ["Tenancy Size", "60 – 500 m² suites & shopfronts"],
      ["Shopfront", "Structural glazing & signage zones"],
      ["Services", "Grease trap, exhaust & 3-phase ready"],
      ["Comfort", "Commercial HVAC & acoustic treatment"],
      ["Access", "DDA-compliant entries & amenities"],
      ["Delivery", "Fast-track fit-outs with live-trade options"],
    ],
    spotlight: {
      title: "Monstone Commercial Kitchen & Juice Bar Fit-Out",
      category: "Shop Fit-Outs / Commercial Hospitality",
      scope: "Custom joinery, counter installation, overhead LED menu boards, hygienic commercial kitchen wall cladding, and council-compliant plumbing/electrical fit-out.",
      status: "100% Completed & Operational",
      image: "/images/projects/card-fitouts.jpg",
    },
  },
  "medical-facilities": {
    name: "Medical Facilities",
    category: "Medical Facilities",
    accent: "#00E676",
    eyebrow: "HEALTHCARE · SPECIALIST SUITES",
    title: "Medical Facilities & Suites",
    tagline: "Clinical-grade builds for NSW's booming healthcare demand.",
    desc: "Purpose-built medical centres and specialist suites — DDA-compliant, infection-control finished, and engineered to the standards healthcare operators demand for long leases.",
    hero: "https://images.pexels.com/photos/2462015/pexels-photo-2462015.jpeg?auto=compress&cs=tinysrgb&w=1920",
    stats: [
      { icon: TrendingUp, value: "~6.5%", label: "Indicative gross yield" },
      { icon: Clock3, value: "~16 mo", label: "Typical build timeline" },
      { icon: BadgeCheck, value: "15 yr", label: "Typical operator lease" },
    ],
    specs: [
      ["Consulting Suites", "20 – 40 m² rooms with handwash stations"],
      ["Procedure Rooms", "Enhanced services & sterile finishes"],
      ["Access", "Full DDA compliance + ambulance bay"],
      ["Infection Control", "Seamless vinyl, coved skirting, HEPA-ready"],
      ["Parking", "High-ratio patient parking & drop-off"],
      ["Compliance", "NCC Class 9a/9c pathways managed"],
    ],
  },
};

const PILLAR_ROUTES = {
  childcare: {
    service: "childcare-centres",
    seo: {
      title: "Turnkey Childcare Centre Builders NSW | JAI HOMES",
      description: "JAI HOMES delivers turnkey childcare centres across Sydney & NSW — DA approvals, play-space engineering and long-term commercial lease structures for high-yield property investors. NSW Builder Lic. 476816C.",
    },
  },
  "gated-communities": {
    service: "multi-unit-developments",
    seo: {
      title: "Masterplanned Gated Community Developers NSW | JAI HOMES",
      description: "Secure, masterplanned gated estates built to exacting standards across NSW — quality estate builds with parks and modern amenity by JAI HOMES, licensed NSW Master Builder 476816C.",
    },
  },
  villas: {
    service: "residential-homes",
    seo: {
      title: "Custom Luxury Villa & Duplex Builders Sydney | JAI HOMES",
      description: "High-end custom villas, duplexes and luxury residential builds across Sydney & NSW — architectural precision and indoor-outdoor living by JAI HOMES. Lic. 476816C.",
    },
  },
  commercial: {
    service: "commercial-shop-fitouts",
    seo: {
      title: "Commercial & Retail Fit-Out Builders NSW | JAI HOMES",
      description: "Medical suites, retail hubs and commercial fit-out construction across NSW — tenant-ready, DDA-compliant delivery by JAI HOMES, NSW Builder Lic. 476816C.",
    },
  },
  "custom-builds": {
    service: "residential-homes",
    seo: {
      title: "Knock-Down & Rebuild Specialists NSW | JAI HOMES",
      description: "Custom requirement-based builds and full knock-down rebuilds on your NSW block — tailored commercial and residential solutions by JAI HOMES, licensed NSW Master Builder, Licence No. 476816C.",
    },
  },
};

const HeroImg = ({ slug, fallback, alt }) => {
  const [src, setSrc] = useState(`/images/projects/${slug}-hero.jpg`);
  return (
    <img
      src={src}
      alt={alt}
      onError={() => src !== fallback && setSrc(fallback)}
      className="absolute inset-0 w-full h-full object-cover animate-kenburns"
    />
  );
};

export default function ServicePage() {
  const { slug } = useParams();
  const direct = SERVICES[slug];
  const pillar = !direct ? PILLAR_ROUTES[slug] : null;
  const svc = direct || (pillar ? SERVICES[pillar.service] : null);
  const seo = direct
    ? { title: `${direct.name} — NSW Master Builder | JAI HOMES`, description: direct.desc }
    : pillar?.seo;
  const { openWizard } = useWizard();

  useEffect(() => {
    if (!svc || !seo) return;
    document.title = seo.title;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", seo.description);
    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", window.location.href);
    setMeta("property", "og:image", svc.hero);
  }, [svc, seo]);

  if (!svc) return <Navigate to="/" replace />;

  return (
    <div className="bg-[#0A0E17] min-h-screen" data-testid={`service-page-${slug}`}>
      <div className="grain-overlay" aria-hidden="true" />
      <Navbar />

      <section className="relative h-[72vh] min-h-[520px] overflow-hidden" data-testid="service-hero">
        <HeroImg slug={slug} fallback={svc.hero} alt={`${svc.name} by JAI HOMES`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-[#0A0E17]/60 to-[#0A0E17]/40" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(55% 45% at 30% 70%, ${svc.accent}1F, transparent 70%)` }}
        />
        <div className="relative z-10 h-full flex flex-col justify-end px-6 sm:px-12 lg:px-20 pb-16 max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Link
              to="/"
              data-testid="service-back-link"
              className="inline-flex items-center gap-2 font-display text-xs font-semibold text-white/60 hover:text-white transition-colors duration-300 mb-6"
            >
              <ArrowLeft size={14} /> Back to Home
            </Link>
            <p className="font-display text-[11px] font-bold tracking-[0.35em] mb-4" style={{ color: svc.accent }}>
              {svc.eyebrow}
            </p>
            <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-6xl text-white leading-[0.98] max-w-3xl" data-testid="service-title">
              {svc.title}
            </h1>
            <p className="mt-5 font-serif-lux italic text-xl sm:text-2xl max-w-2xl" style={{ color: svc.accent }}>
              {svc.tagline}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-sm sm:text-base text-white/65 leading-relaxed max-w-3xl"
          data-testid="service-desc"
        >
          {svc.desc}
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12" data-testid="service-roi-band">
          {svc.stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="glass rounded-2xl border border-white/10 p-6 hover:-translate-y-1 transition-transform duration-300"
                style={{ boxShadow: `0 0 40px ${svc.accent}0D` }}
                data-testid={`service-stat-${i}`}
              >
                <Icon size={20} style={{ color: svc.accent }} className="mb-3" />
                <p className="font-display text-3xl font-extrabold text-white tracking-tight">{s.value}</p>
                <p className="text-xs text-white/50 mt-1">{s.label} — indicative only</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 pb-20 max-w-7xl mx-auto" data-testid="service-specs">
        <h2 className="font-display font-extrabold tracking-tighter text-2xl sm:text-3xl text-white mb-8">
          Typical floorplan &amp; delivery specs
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {svc.specs.map(([label, value], i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 border-l-2 hover:bg-white/[0.05] transition-colors duration-300"
              style={{ borderLeftColor: svc.accent }}
              data-testid={`service-spec-${i}`}
            >
              <p className="font-display text-[11px] font-bold tracking-[0.2em] text-white/45 mb-1.5">{label.toUpperCase()}</p>
              <p className="text-sm text-white/85 leading-relaxed">{value}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {svc.spotlight && (
        <section className="px-6 sm:px-12 lg:px-20 pb-20 max-w-7xl mx-auto" data-testid="service-spotlight">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="glass rounded-3xl border border-white/10 overflow-hidden grid grid-cols-1 lg:grid-cols-2"
          >
            <div className="relative h-72 lg:h-auto lg:min-h-[380px]">
              <img
                src={svc.spotlight.image}
                alt={svc.spotlight.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="spotlight-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17]/70 via-transparent to-transparent" />
              <span
                className="absolute bottom-5 left-5 rounded-full px-4 py-2 font-display text-[11px] font-bold tracking-[0.15em] text-[#0A0E17] bg-[#00E676] shadow-[0_0_20px_rgba(0,230,118,0.5)]"
                data-testid="spotlight-status"
              >
                {svc.spotlight.status.toUpperCase()}
              </span>
            </div>
            <div className="p-8 sm:p-10 flex flex-col justify-center">
              <p className="font-display text-[11px] font-bold tracking-[0.3em] mb-3" style={{ color: svc.accent }}>
                DELIVERED PROJECT — {svc.spotlight.category.toUpperCase()}
              </p>
              <h2 className="font-display font-extrabold tracking-tighter text-2xl sm:text-3xl text-white leading-tight">
                {svc.spotlight.title}
              </h2>
              <p className="mt-4 text-sm text-white/60 leading-relaxed">{svc.spotlight.scope}</p>
              <p className="mt-5 inline-flex items-center gap-2 text-xs text-white/50">
                <BadgeCheck size={15} style={{ color: svc.accent }} />
                Delivered by JAI HOMES · NSW Builder Licence 476816C
              </p>
            </div>
          </motion.div>
        </section>
      )}

      <section className="px-6 sm:px-12 lg:px-20 pb-28 max-w-7xl mx-auto">
        <div
          className="glass rounded-3xl border p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8"
          style={{ borderColor: `${svc.accent}44`, boxShadow: `0 0 80px ${svc.accent}14` }}
          data-testid="service-cta-band"
        >
          <div>
            <h2 className="font-display font-extrabold tracking-tighter text-3xl sm:text-4xl text-white leading-tight">
              Ready to talk{" "}
              <em className="font-serif-lux italic font-semibold" style={{ color: svc.accent }}>
                {svc.name.toLowerCase()}
              </em>
              ?
            </h2>
            <p className="mt-3 text-sm text-white/60 max-w-lg leading-relaxed">
              Fire off a pre-filled project query — our engineering team responds within 24 hours.
              NSW Builder Licence 476816C.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={() => openWizard(svc.category)}
              data-testid="service-wizard-cta"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-display text-sm font-bold text-white bg-gradient-to-r from-[#FF3D00] to-[#FF1744] hover:shadow-[0_0_32px_rgba(255,23,68,0.55)] hover:scale-[1.03] transition-all duration-300"
            >
              Start a {svc.name} Query <ArrowRight size={16} />
            </button>
            <a
              href="tel:0272395450"
              data-testid="service-phone-cta"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-display text-sm font-semibold text-white/80 border border-white/20 hover:border-white/50 hover:text-white transition-colors duration-300"
            >
              <Phone size={16} /> (02) 7239 5450
            </a>
          </div>
        </div>
      </section>

      <MobileCtaBar />
      <Footer />
    </div>
  );
}
